import React, { useEffect, useState } from "react";
import oraclePhase27Api from "@/api/oraclePhase27Client";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — LOGIX-2 Recommendation Card (Phase 27I).
 *
 * Structured CareLogix-native recommendation derived from the ORACLE SCORE™
 * envelope. Shows verdict (buy/hold/watch/avoid), entry range, stop-loss,
 * target, risk amount, confidence, and reasons. HITL only — never auto-trades.
 */

const VERDICT_TO_ACTION = {
  watch_strong: { code: "WATCH",   label: "Watch — strong setup", color: "#10b981", advice: "Build watchlist; confirm setup with primary source before any action." },
  watch:        { code: "WATCH",   label: "Watch — promising",     color: "#22c55e", advice: "Track for clean trigger; await confirmation candle / catalyst." },
  consider:     { code: "CONSIDER",label: "Consider — mixed",      color: "#fcd34d", advice: "Probabilistic — reduce size; require multi-factor confirmation." },
  hold:         { code: "HOLD",    label: "Hold — weak signal",    color: "#94a3b8", advice: "No new exposure; existing positions may hold with tight stops." },
  avoid:        { code: "AVOID",   label: "Avoid — risk > reward", color: "#ef4444", advice: "Step aside until factors align; do not chase." },
};

function fmtUsd(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function fmtPct(v) {
  if (typeof v !== "number") return "—";
  return `${v.toFixed(1)}%`;
}

/**
 * Derive an indicative entry/stop/target plan from the ORACLE SCORE data.
 * Conservative defaults — no autonomous execution.
 */
function deriveTradePlan(score, accountSize = 10_000) {
  const composite = Number(score?.oracle_score || 0);
  const verdict = score?.verdict || "consider";
  const direction = score?.direction || "neutral";
  // Position risk is risk% of account capped at 1.5% for non-watch_strong.
  const riskPct = verdict === "watch_strong" ? 1.0 : (verdict === "watch" ? 0.75 : (verdict === "consider" ? 0.50 : 0.0));
  const riskUsd = +(accountSize * riskPct / 100).toFixed(2);
  // Entry/SL/Target percentages relative to last close.
  const entryFromPct = direction === "bearish" ? +0.4 : -0.3;
  const entryToPct   = direction === "bearish" ? +0.9 : +0.4;
  const stopPct      = direction === "bearish" ? +1.6 : -1.4;
  const targetPct    = direction === "bearish" ? -2.6 : +2.6;
  const rr = Math.abs(targetPct) / Math.max(Math.abs(stopPct), 0.0001);
  return {
    entry_from_pct: entryFromPct,
    entry_to_pct:   entryToPct,
    stop_pct:       stopPct,
    target_pct:     targetPct,
    risk_pct:       riskPct,
    risk_usd:       riskUsd,
    reward_risk:    +rr.toFixed(2),
    direction,
    note: direction === "bearish"
      ? "Short bias (research only). Use put options / inverse exposure where legal."
      : (direction === "bullish" ? "Long bias. Consider tranching entries inside the band." : "Sideways bias. Reduce size; await directional break."),
  };
}

export default function Logix2RecommendationCard({
  symbol = "AAPL",
  assetClass = "stocks",
  accountSize = 10_000,
  userName = null,
  testid = "logix2-recommendation",
}) {
  const [score, setScore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr("");
    oraclePhase27Api.score(symbol, assetClass)
      .then(({ data }) => { if (alive) setScore(data); })
      .catch((e) => { if (alive) setErr(e?.message || "score_failed"); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [symbol, assetClass]);

  if (busy && !score) return <div style={S.shell} data-testid={`${testid}-loading`}>LOGIX-2 is generating a structured recommendation…</div>;
  if (err) return <div style={S.shell} data-testid={`${testid}-error`}>⚠ {err}</div>;
  if (!score) return null;

  const action = VERDICT_TO_ACTION[score.verdict] || VERDICT_TO_ACTION.consider;
  const plan = deriveTradePlan(score, accountSize);
  const topReasons = score.sub_scores
    .filter((s) => s.score >= 60)
    .slice(0, 3)
    .map((s) => `${s.label}: ${s.reason} (score ${s.score})`);
  const cautions = score.sub_scores
    .filter((s) => s.score < 45)
    .slice(0, 2)
    .map((s) => `${s.label}: ${s.reason} (score ${s.score})`);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>LOGIX-2™ Recommendation</span>
          <h3 style={S.title}>
            {userName ? `${userName}, ` : ""}LOGIX-2 plan for <code>{score.symbol}</code> · <small style={{ color: "#94a3b8" }}>{score.asset_class}</small>
          </h3>
        </div>
        <span
          data-testid={`${testid}-action`}
          style={{
            ...S.actionPill,
            color: action.color,
            borderColor: action.color,
            background: `${action.color}1a`,
          }}
        >{action.code}</span>
      </header>

      <p style={S.advice}>{action.label}. {action.advice}</p>

      <div style={S.grid}>
        <div style={S.cell}>
          <span style={S.cellLbl}>ENTRY RANGE</span>
          <strong style={{ ...S.cellVal, color: "#c5a059" }} data-testid={`${testid}-entry`}>
            {fmtPct(plan.entry_from_pct)} → {fmtPct(plan.entry_to_pct)}
          </strong>
          <span style={S.cellSub}>relative to last close</span>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>STOP-LOSS</span>
          <strong style={{ ...S.cellVal, color: "#ef4444" }} data-testid={`${testid}-stop`}>{fmtPct(plan.stop_pct)}</strong>
          <span style={S.cellSub}>discipline level</span>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>TARGET</span>
          <strong style={{ ...S.cellVal, color: "#10b981" }} data-testid={`${testid}-target`}>{fmtPct(plan.target_pct)}</strong>
          <span style={S.cellSub}>research-only objective</span>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>RISK</span>
          <strong style={{ ...S.cellVal, color: "#fcd34d" }} data-testid={`${testid}-risk`}>
            {plan.risk_pct.toFixed(2)}% · {fmtUsd(plan.risk_usd)}
          </strong>
          <span style={S.cellSub}>on ${accountSize.toLocaleString()} bankroll</span>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>CONFIDENCE</span>
          <strong style={{ ...S.cellVal, color: "#67e8f9" }} data-testid={`${testid}-confidence`}>
            {score.meta?.mean_confidence}%
          </strong>
          <span style={S.cellSub}>multi-factor avg</span>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>REWARD : RISK</span>
          <strong style={{ ...S.cellVal, color: plan.reward_risk >= 1.5 ? "#10b981" : "#fca5a5" }} data-testid={`${testid}-rr`}>
            {plan.reward_risk}:1
          </strong>
          <span style={S.cellSub}>≥1.5 preferred</span>
        </div>
      </div>

      <div style={S.lists}>
        <div style={S.list}>
          <strong style={{ color: "#10b981" }}>Reasons LOGIX-2 is watching:</strong>
          {topReasons.length > 0 ? (
            <ul style={S.ul}>
              {topReasons.map((r, i) => <li key={`r-${i}`} data-testid={`${testid}-reason-${i}`}>{r}</li>)}
            </ul>
          ) : <p style={S.empty}>No factors above 60. Wait for stronger alignment.</p>}
        </div>
        <div style={S.list}>
          <strong style={{ color: "#fcd34d" }}>Uncertainty warnings:</strong>
          {cautions.length > 0 ? (
            <ul style={S.ul}>
              {cautions.map((c, i) => <li key={`c-${i}`} data-testid={`${testid}-caution-${i}`}>{c}</li>)}
            </ul>
          ) : <p style={S.empty}>No sub-factor below 45. Residual uncertainty {score.meta?.mean_uncertainty}%.</p>}
        </div>
      </div>

      <p style={S.note}><b>Note from LOGIX-2:</b> {plan.note} I will never place trades or wagers for you. You must confirm any action.</p>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: {
    background: "linear-gradient(135deg, rgba(11,15,20,0.96), rgba(8,32,22,0.92))",
    border: "1px solid rgba(197,160,89,0.30)",
    borderRadius: 14,
    padding: 14,
    color: "#e2e8f0",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 16 },
  actionPill: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.18em",
    padding: "6px 14px",
    border: "2px solid",
    borderRadius: 999,
  },
  advice: { color: "#cbd5e1", fontSize: 13, lineHeight: 1.5, margin: 0 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 },
  cell: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 3 },
  cellLbl: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em" },
  cellVal: { fontFamily: "'Space Mono', monospace", fontSize: 16, fontWeight: 800 },
  cellSub: { color: "#94a3b8", fontSize: 10.5, fontStyle: "italic" },
  lists: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  list: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10 },
  ul: { margin: "4px 0 0", paddingLeft: 18, color: "#cbd5e1", fontSize: 12.5, lineHeight: 1.5 },
  empty: { color: "#94a3b8", margin: 0, fontSize: 11.5, fontStyle: "italic" },
  note: { color: "#fde68a", fontSize: 12, lineHeight: 1.5, margin: 0, padding: "8px 10px", background: "rgba(197,160,89,0.08)", border: "1px solid rgba(197,160,89,0.30)", borderRadius: 10 },
};
